import React, { useEffect, useRef, useState } from 'react';
import MainLayout from '../components/MainLayout';
import { Spinner } from 'react-bootstrap';
import { Plus, Pencil, Copy, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { cashMarketAPI, CashMarketDirection, CashMarketConfig, CashMarketBracket } from '../services/cashMarketApi';
import CashMarketConfigForm from '../components/CashMarketConfigForm';

// Multiple named configs per direction (like NY/SX Spread's config list) — each config row can
// be expanded to show its own Position Details + Open Positions, since positions belong to one
// specific config now, not to "the" direction as a whole.

const ACCENT: Record<CashMarketDirection, { border: string; bg: string; text: string; label: string }> = {
  BUY_SELL: { border: '#a7f3d0', bg: '#ecfdf5', text: '#065f46', label: 'BUY → SELL' },
  SELL_BUY: { border: '#fecaca', bg: '#fef2f2', text: '#991b1b', label: 'SELL → BUY' },
};

interface OpenPosition {
  id: number;
  symbol: string;
  transaction_type: 'BUY' | 'SELL';
  quantity: number;
  price: number;
  entry_ltp: number;
  order_status: string;
  bracket: CashMarketBracket | null;
  current_ltp: number | null;
  pnl: number | null;
  sl_percentage: number | null;
  dynamic_sl_percentage: number | null;
  peak_ltp: number | null;
  broker?: string;
}

// Effective SL price level for one position — mirrors cashMarketService.checkAndAutoExitSL's own
// math exactly: anchored to entry_ltp normally, or to peak_ltp once TSL has ratcheted
// (dynamic_sl_percentage set), so what's shown here is the real level the auto-exit checks against.
const computeSlLevel = (p: OpenPosition): number | null => {
  const isLong = p.transaction_type === 'BUY';
  const dynPct = p.dynamic_sl_percentage !== null ? Number(p.dynamic_sl_percentage) : 0;
  const basePct = p.sl_percentage !== null ? Number(p.sl_percentage) : 0;
  const entryLtp = Number(p.entry_ltp) || 0;
  if (dynPct > 0) {
    const anchor = Number(p.peak_ltp) || entryLtp;
    return isLong ? anchor - (anchor * dynPct / 100) : anchor + (anchor * dynPct / 100);
  }
  if (basePct > 0 && entryLtp > 0) {
    return isLong ? entryLtp - (entryLtp * basePct / 100) : entryLtp + (entryLtp * basePct / 100);
  }
  return null;
};

const labelStyle: React.CSSProperties = { display: 'block', fontSize: '11px', fontWeight: 500, color: '#6b7280' };
const valueStyle: React.CSSProperties = { fontSize: '13px', fontWeight: 600, color: '#111827' };

interface ConfigCardProps {
  config: CashMarketConfig;
  accent: typeof ACCENT[CashMarketDirection];
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
}

const ConfigCard: React.FC<ConfigCardProps> = ({ config, accent, onEdit, onDuplicate, onDelete, onToggleActive }) => {
  const [expanded, setExpanded] = useState(false);
  const [positions, setPositions] = useState<OpenPosition[]>([]);
  const [placing, setPlacing] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [exitingOrderId, setExitingOrderId] = useState<number | null>(null);
  const [exitingBracket, setExitingBracket] = useState<CashMarketBracket | null>(null);
  const [exitingLoss, setExitingLoss] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [sortField, setSortField] = useState<'entry_ltp' | 'symbol' | 'quantity'>('entry_ltp');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [bracketFilter, setBracketFilter] = useState<'ALL' | CashMarketBracket>('ALL');

  const fetchPositions = async () => {
    try {
      const res = await cashMarketAPI.getOpenPositions(config.id);
      setPositions(res.data);
    } catch (err: any) {
      console.error('[CashMarket] Failed to fetch positions:', err.message);
    }
  };

  useEffect(() => {
    fetchPositions();
    const interval = setInterval(fetchPositions, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.id]);

  const stocksInLoss = positions.filter(p => p.pnl !== null && p.pnl < 0).length;

  const handleSort = (field: 'entry_ltp' | 'symbol' | 'quantity') => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc'); // default small-to-high / A-Z whenever a new column is picked
    }
  };
  const sortArrow = (field: typeof sortField) => (sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const displayPositions = positions
    .filter(p => bracketFilter === 'ALL' || p.bracket === bracketFilter)
    .slice()
    .sort((a, b) => {
      const cmp = sortField === 'symbol' ? a.symbol.localeCompare(b.symbol) : a[sortField] - b[sortField];
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const handlePlace = async () => {
    setPlacing(true); setMessage(null);
    try {
      const res = await cashMarketAPI.placeOrder(config.id);
      setMessage({ type: 'success', text: res.data.message });
      fetchPositions();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setPlacing(false);
    }
  };

  const handleExitAll = async () => {
    setExiting(true); setMessage(null);
    try {
      const res = await cashMarketAPI.exitAll(config.id);
      setMessage({ type: 'success', text: res.data.message });
      fetchPositions();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setExiting(false);
    }
  };

  const handleExitOne = async (orderId: number) => {
    setExitingOrderId(orderId);
    try {
      await cashMarketAPI.exitPosition(orderId);
      fetchPositions();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setExitingOrderId(null);
    }
  };

  const handleExitBracket = async (bracket: CashMarketBracket) => {
    setExitingBracket(bracket);
    try {
      const res = await cashMarketAPI.exitBracket(config.id, bracket);
      setMessage({ type: 'success', text: res.data.message });
      fetchPositions();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setExitingBracket(null);
    }
  };

  const handleExitLoss = async () => {
    setExitingLoss(true);
    try {
      const res = await cashMarketAPI.exitLossMaking(config.id);
      setMessage({ type: 'success', text: res.data.message });
      fetchPositions();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message });
    } finally {
      setExitingLoss(false);
    }
  };

  return (
    <div style={{ border: `1px solid ${accent.border}`, borderRadius: '8px', marginBottom: '10px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: accent.bg }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }} onClick={() => setExpanded(!expanded)}>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          <span style={{ fontWeight: 700, fontSize: '13px', color: accent.text }}>{config.name}</span>
          <span style={{ fontSize: '11px', color: '#6b7280' }}>
            X:{config.entry_x} Y:{config.entry_y} · Invest ₹{config.invest_amount_k} · Stocks:{config.no_of_stocks} · Open:{positions.length}
            {(() => {
              // Live ROI% — total P&L ÷ total invested across this config's open positions, same
              // money-weighted formula the backend's auto-exit circuit-breaker checks against.
              // Not a field anyone types in — it only exists once real money is on the table.
              const invested = positions.reduce((sum, p) => sum + (Number(p.entry_ltp) || 0) * (Number(p.quantity) || 0), 0);
              const pnl = positions.reduce((sum, p) => sum + (Number(p.pnl) || 0), 0);
              if (invested <= 0) return null;
              const roi = (pnl / invested) * 100;
              return <> · ROI:<span style={{ color: roi >= 0 ? '#059669' : '#dc2626', fontWeight: 700 }}>{roi >= 0 ? '+' : ''}{roi.toFixed(2)}%</span></>;
            })()}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 600, color: accent.text, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!config.is_active} onChange={onToggleActive} />
            {config.is_active ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={onEdit} title="Edit" style={{ padding: '4px', border: 'none', background: 'transparent', cursor: 'pointer' }}><Pencil size={14} /></button>
          <button onClick={onDuplicate} title="Duplicate" style={{ padding: '4px', border: 'none', background: 'transparent', cursor: 'pointer' }}><Copy size={14} /></button>
          <button onClick={onDelete} title="Delete" style={{ padding: '4px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#991b1b' }}><Trash2 size={14} /></button>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '14px', background: 'white' }}>
          {message && (
            <div style={{
              padding: '6px 10px', borderRadius: '4px', marginBottom: '10px', fontSize: '12px',
              background: message.type === 'success' ? '#ecfdf5' : '#fef2f2',
              color: message.type === 'success' ? '#065f46' : '#991b1b',
              border: `1px solid ${message.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
            }}>
              {message.text}
              <button onClick={() => setMessage(null)} style={{ float: 'right', border: 'none', background: 'transparent', cursor: 'pointer' }}>×</button>
            </div>
          )}

          <div style={{ display: 'flex', gap: '20px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div><label style={labelStyle}>SL / TGT / TSL</label><div style={valueStyle}>{config.sl_percentage}% / {config.target_percentage}% / {config.tsl_percentage}%</div></div>
            <div><label style={labelStyle}>ROI Exit</label><div style={valueStyle}>{config.roi_exit_percentage}%</div></div>
            <div>
              <label style={labelStyle}>Stocks in Loss</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ ...valueStyle, color: stocksInLoss > 0 ? '#991b1b' : '#111827' }}>{stocksInLoss}</div>
                <button
                  onClick={handleExitLoss} disabled={exitingLoss || stocksInLoss === 0}
                  style={{ padding: '3px 8px', fontSize: '10px', fontWeight: 600, color: '#991b1b', background: 'white', border: '1px solid #fca5a5', borderRadius: '4px', cursor: stocksInLoss === 0 ? 'default' : 'pointer', opacity: stocksInLoss === 0 ? 0.5 : 1 }}
                >
                  {exitingLoss ? '...' : 'Exit Loss'}
                </button>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Exit by Bracket</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                {(['P1', 'P2', 'P3'] as CashMarketBracket[]).map(bracket => {
                  const hasStock = positions.some(p => p.bracket === bracket);
                  const disabled = exitingBracket === bracket || !hasStock;
                  return (
                    <button
                      key={bracket} onClick={() => handleExitBracket(bracket)} disabled={disabled}
                      title={hasStock ? undefined : `No open stock in ${bracket}`}
                      style={{
                        padding: '3px 8px', fontSize: '10px', fontWeight: 600,
                        color: hasStock ? '#581c87' : '#9ca3af',
                        background: 'white', border: `1px solid ${hasStock ? '#c4b5fd' : '#e5e7eb'}`,
                        borderRadius: '4px', cursor: disabled ? 'default' : 'pointer',
                        opacity: hasStock ? 1 : 0.6,
                      }}
                    >
                      {exitingBracket === bracket ? '...' : `Exit ${bracket}`}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
            <button onClick={handlePlace} disabled={placing} style={{ padding: '7px 16px', fontSize: '12px', fontWeight: 600, color: 'white', background: placing ? '#93c5fd' : '#0d6efd', border: 'none', borderRadius: '4px', cursor: placing ? 'default' : 'pointer' }}>
              {placing ? <Spinner size="sm" animation="border" /> : 'Place Order'}
            </button>
            <button onClick={handleExitAll} disabled={exiting} style={{ padding: '7px 16px', fontSize: '12px', fontWeight: 600, color: '#374151', background: 'white', border: '1px solid #9ca3af', borderRadius: '4px', cursor: exiting ? 'default' : 'pointer' }}>
              {exiting ? <Spinner size="sm" animation="border" /> : 'Exit All'}
            </button>
          </div>

          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '4px 6px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('symbol')}>
                  Symbol{sortArrow('symbol')}
                </th>
                <th style={{ padding: '4px 6px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('quantity')}>
                  Qty{sortArrow('quantity')}
                </th>
                <th style={{ padding: '4px 6px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('entry_ltp')}>
                  Entry LTP{sortArrow('entry_ltp')}
                </th>
                <th style={{ padding: '4px 6px' }}>LTP</th>
                <th style={{ padding: '4px 6px' }}>SL</th>
                <th style={{ padding: '4px 6px' }}>P&amp;L</th>
                <th style={{ padding: '4px 6px' }}>
                  <select
                    value={bracketFilter} onChange={e => setBracketFilter(e.target.value as any)}
                    style={{ fontSize: '11px', fontWeight: 600, border: 'none', background: 'transparent', cursor: 'pointer' }}
                  >
                    <option value="ALL">Bracket</option>
                    <option value="P1">P1</option>
                    <option value="P2">P2</option>
                    <option value="P3">P3</option>
                  </select>
                </th>
                <th style={{ padding: '4px 6px' }}>Status</th>
                <th style={{ padding: '4px 6px' }}>Broker</th>
                <th style={{ padding: '4px 6px' }}></th>
              </tr>
            </thead>
            <tbody>
              {displayPositions.length === 0 && (
                <tr><td colSpan={10} style={{ padding: '10px 6px', color: '#9ca3af' }}>No open positions</td></tr>
              )}
              {displayPositions.map(p => {
                const slLevel = computeSlLevel(p);
                return (
                <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '4px 6px', fontWeight: 600 }}>{p.symbol}</td>
                  <td style={{ padding: '4px 6px' }}>{p.quantity}</td>
                  <td style={{ padding: '4px 6px' }}>₹{p.entry_ltp}</td>
                  <td style={{ padding: '4px 6px' }}>{p.current_ltp !== null ? `₹${p.current_ltp}` : '—'}</td>
                  <td style={{ padding: '4px 6px', color: '#991b1b' }}>{slLevel !== null ? `₹${slLevel.toFixed(2)}` : '—'}</td>
                  <td style={{ padding: '4px 6px', color: p.pnl === null ? '#6b7280' : p.pnl < 0 ? '#991b1b' : '#065f46', fontWeight: 600 }}>
                    {p.pnl !== null ? `${p.pnl >= 0 ? '+' : ''}₹${p.pnl.toFixed(2)}` : '—'}
                  </td>
                  <td style={{ padding: '4px 6px' }}>{p.bracket || '—'}</td>
                  <td style={{ padding: '4px 6px' }}>{p.order_status}</td>
                  <td style={{ padding: '4px 6px' }}>
                    <span style={{
                      fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '10px',
                      background: p.broker === 'GROWW' ? '#ecfdf5' : '#eff6ff',
                      color: p.broker === 'GROWW' ? '#065f46' : '#1d4ed8',
                    }}>
                      {p.broker || 'DHAN'}
                    </span>
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <button
                      onClick={() => handleExitOne(p.id)} disabled={exitingOrderId === p.id}
                      style={{ padding: '3px 8px', fontSize: '11px', fontWeight: 600, color: '#991b1b', background: 'white', border: '1px solid #fca5a5', borderRadius: '4px', cursor: exitingOrderId === p.id ? 'default' : 'pointer' }}
                    >
                      {exitingOrderId === p.id ? '...' : 'Exit'}
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const DirectionColumn: React.FC<{
  direction: CashMarketDirection;
  configs: CashMarketConfig[];
  onAdd: () => void;
  onEdit: (c: CashMarketConfig) => void;
  onDuplicate: (c: CashMarketConfig) => void;
  onDelete: (c: CashMarketConfig) => void;
  onToggleActive: (c: CashMarketConfig) => void;
}> = ({ direction, configs, onAdd, onEdit, onDuplicate, onDelete, onToggleActive }) => {
  const accent = ACCENT[direction];
  return (
    <div style={{ flex: 1, minWidth: '420px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: 700, color: accent.text, margin: 0 }}>{accent.label}</h4>
        <button
          onClick={onAdd}
          style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, color: 'white', background: '#6f42c1', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          <Plus size={14} /> Add Config
        </button>
      </div>
      {configs.length === 0 && (
        <div style={{ padding: '16px', color: '#9ca3af', fontSize: '13px', border: '1px dashed #d1d5db', borderRadius: '8px' }}>
          No configs yet — click "Add Config" to create one.
        </div>
      )}
      {configs.map(c => (
        <ConfigCard
          key={c.id} config={c} accent={accent}
          onEdit={() => onEdit(c)} onDuplicate={() => onDuplicate(c)}
          onDelete={() => onDelete(c)} onToggleActive={() => onToggleActive(c)}
        />
      ))}
    </div>
  );
};

const CashMarketPage: React.FC = () => {
  const [configs, setConfigs] = useState<CashMarketConfig[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [formDirection, setFormDirection] = useState<CashMarketDirection>('BUY_SELL');
  const [editingConfig, setEditingConfig] = useState<CashMarketConfig | null>(null);
  const [placingAll, setPlacingAll] = useState(false);
  const [exitingAll, setExitingAll] = useState(false);
  const [bulkMessage, setBulkMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchConfigs = async () => {
    try {
      const res = await cashMarketAPI.getConfigs();
      setConfigs(res.data);
    } catch (err: any) {
      console.error('[CashMarket] Failed to fetch configs:', err.message);
    }
  };

  useEffect(() => {
    fetchConfigs();
    pollRef.current = setInterval(fetchConfigs, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const openCreateForm = (direction: CashMarketDirection) => {
    setFormDirection(direction);
    setEditingConfig(null);
    setShowForm(true);
  };
  const openEditForm = (c: CashMarketConfig) => {
    setFormDirection(c.direction);
    setEditingConfig(c);
    setShowForm(true);
  };

  const handleSubmit = async (data: Partial<CashMarketConfig>) => {
    try {
      if (editingConfig) {
        await cashMarketAPI.updateConfig(editingConfig.id, data);
      } else {
        await cashMarketAPI.createConfig(data);
      }
      await fetchConfigs();
      setShowForm(false);
      setEditingConfig(null);
    } catch (err: any) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleDuplicate = async (c: CashMarketConfig) => {
    try {
      await cashMarketAPI.duplicateConfig(c.id);
      fetchConfigs();
    } catch (err: any) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleDelete = async (c: CashMarketConfig) => {
    if (!window.confirm(`Delete config "${c.name}"?`)) return;
    try {
      await cashMarketAPI.deleteConfig(c.id);
      fetchConfigs();
    } catch (err: any) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleToggleActive = async (c: CashMarketConfig) => {
    try {
      await cashMarketAPI.toggleActive(c.id, !c.is_active);
      fetchConfigs();
    } catch (err: any) {
      alert(err.response?.data?.error || err.message);
    }
  };

  // Top-level "Place Order (Both)" / "Exit (Both)" from the old fixed 2-panel layout — now that
  // a direction can hold several configs, this fires the same action across every ENABLED
  // config in both directions at once, instead of exactly one BUY_SELL + one SELL_BUY row.
  const handlePlaceAll = async () => {
    const enabled = configs.filter(c => c.is_active);
    if (enabled.length === 0) { setBulkMessage({ type: 'error', text: 'No enabled configs' }); return; }
    setPlacingAll(true); setBulkMessage(null);
    try {
      const results = await Promise.allSettled(enabled.map(c => cashMarketAPI.placeOrder(c.id)));
      const failed = results.filter(r => r.status === 'rejected') as PromiseRejectedResult[];
      setBulkMessage(failed.length === 0
        ? { type: 'success', text: `Entry triggered for ${enabled.length} config(s)` }
        : { type: 'error', text: `${enabled.length - failed.length}/${enabled.length} triggered. Errors: ${failed.map(f => f.reason?.response?.data?.error || f.reason?.message).join('; ')}` });
    } finally {
      setPlacingAll(false);
    }
  };

  const handleExitAllConfigs = async () => {
    const enabled = configs.filter(c => c.is_active);
    if (enabled.length === 0) { setBulkMessage({ type: 'error', text: 'No enabled configs' }); return; }
    setExitingAll(true); setBulkMessage(null);
    try {
      const results = await Promise.allSettled(enabled.map(c => cashMarketAPI.exitAll(c.id)));
      const failed = results.filter(r => r.status === 'rejected') as PromiseRejectedResult[];
      setBulkMessage(failed.length === 0
        ? { type: 'success', text: `Exit executed for ${enabled.length} config(s)` }
        : { type: 'error', text: `${enabled.length - failed.length}/${enabled.length} exited. Errors: ${failed.map(f => f.reason?.response?.data?.error || f.reason?.message).join('; ')}` });
    } finally {
      setExitingAll(false);
    }
  };

  return (
    <MainLayout>
      <div style={{ padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '20px', marginBottom: '10px' }}>
          <h5 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>Cash Market (Equity Intraday)</h5>
          <button
            onClick={handlePlaceAll} disabled={placingAll}
            style={{ padding: '10px 24px', fontSize: '13px', fontWeight: 700, color: 'white', background: placingAll ? '#a78bda' : '#6f42c1', border: 'none', borderRadius: '4px', cursor: placingAll ? 'default' : 'pointer' }}
          >
            {placingAll ? <Spinner size="sm" animation="border" /> : 'Place Order (All Enabled)'}
          </button>
          <button
            onClick={handleExitAllConfigs} disabled={exitingAll}
            style={{ padding: '10px 24px', fontSize: '13px', fontWeight: 700, color: '#374151', background: 'white', border: '1px solid #9ca3af', borderRadius: '4px', cursor: exitingAll ? 'default' : 'pointer' }}
          >
            {exitingAll ? <Spinner size="sm" animation="border" /> : 'Exit (All Enabled)'}
          </button>
        </div>

        {bulkMessage && (
          <div style={{
            padding: '8px 12px', borderRadius: '4px', marginBottom: '12px', fontSize: '13px',
            background: bulkMessage.type === 'success' ? '#ecfdf5' : '#fef2f2',
            color: bulkMessage.type === 'success' ? '#065f46' : '#991b1b',
            border: `1px solid ${bulkMessage.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
          }}>
            {bulkMessage.text}
            <button onClick={() => setBulkMessage(null)} style={{ float: 'right', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '13px', color: 'inherit' }}>×</button>
          </div>
        )}

        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <DirectionColumn
            direction="BUY_SELL" configs={configs.filter(c => c.direction === 'BUY_SELL')}
            onAdd={() => openCreateForm('BUY_SELL')} onEdit={openEditForm}
            onDuplicate={handleDuplicate} onDelete={handleDelete} onToggleActive={handleToggleActive}
          />
          <DirectionColumn
            direction="SELL_BUY" configs={configs.filter(c => c.direction === 'SELL_BUY')}
            onAdd={() => openCreateForm('SELL_BUY')} onEdit={openEditForm}
            onDuplicate={handleDuplicate} onDelete={handleDelete} onToggleActive={handleToggleActive}
          />
        </div>

        {showForm && (
          <CashMarketConfigForm
            direction={formDirection}
            existing={editingConfig}
            onSubmit={handleSubmit}
            onCancel={() => { setShowForm(false); setEditingConfig(null); }}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default CashMarketPage;
